import React from 'react';

import { Link } from 'react-router-dom';

import { type CardSizes } from '@pkg/components';
import { CollectionLink } from '@pkg/components/Collections/CollectionLink.tsx';
import { type Collectible, type Collection } from '@pkg/graphql/codegen/graphql';

import { HoverUnderline } from '@app/pages/NFTDetailView/components/MainStage/components/NFTInfo/styles';

import { CollectionName, Info, Inside, NftName, Thumbnail, ThumbnailSkeleton, Wrapper } from './styles';

interface CardNameProps {
    cardSize?: CardSizes;
    hideCollectionData?: boolean;
    collectionData?: Collection | null;
    item?: Collectible | null;
}

export const CardName = ({ item, hideCollectionData, cardSize, collectionData }: CardNameProps) => {
    const { name, id } = item || {};
    const [chain, contract, _tokenId, _aptosTokenId] = id?.split('.') || [];
    const aptosTokenId = _aptosTokenId && collectionData?.name ? `${collectionData.name}.${_aptosTokenId}` : undefined;
    const tokenId = aptosTokenId || _tokenId;
    const collectionSlug = collectionData?.slug || `${contract}.${collectionData?.name}`;
    const collectionThumbMarkup =
        cardSize !== 'small' && !hideCollectionData ? (
            !collectionData?.imageUrl ? (
                <ThumbnailSkeleton />
            ) : (
                <CollectionLink slug={collectionSlug} chain={chain} collectionData={collectionData} disablePeek>
                    <Thumbnail title={collectionData?.name || ''} $image={collectionData?.imageUrl} />
                </CollectionLink>
            )
        ) : null;

    const collectionNameMarkup =
        !hideCollectionData && collectionData?.name ? (
            <CollectionName title={collectionData?.name} $cardSize={cardSize}>
                <CollectionLink slug={collectionSlug} chain={chain} collectionData={collectionData}>
                    {collectionData?.name}
                    {collectionSlug && <HoverUnderline />}
                </CollectionLink>
            </CollectionName>
        ) : null;

    const showItemNameWithCollectionStyle = hideCollectionData || !collectionData?.name;

    const collectibleUrlPath = `${chain}/${contract}/${encodeURIComponent(tokenId)}`;
    const collectibleUrl = `/collectible/${collectibleUrlPath}?goBack=1`;
    const canRenderLink = Boolean(chain?.length && contract?.length && tokenId?.length);
    /* show NftName using CollectionName style */
    const itemNameMarkup = name ? (
        showItemNameWithCollectionStyle ? (
            <CollectionName title={name || ''} $cardSize={cardSize}>
                {canRenderLink ? (
                    <Link to={collectibleUrl}>
                        {name} {collectionSlug && <HoverUnderline />}
                    </Link>
                ) : (
                    name
                )}
            </CollectionName>
        ) : (
            <NftName title={name} $cardSize={cardSize}>
                {canRenderLink ? <Link to={collectibleUrl}>{name}</Link> : name}
            </NftName>
        )
    ) : null;

    return (
        <Wrapper $cardSize={cardSize}>
            <Inside $setHeight={!hideCollectionData} $cardSize={cardSize}>
                {collectionThumbMarkup}
                <Info>
                    {collectionNameMarkup}
                    {itemNameMarkup}
                </Info>
            </Inside>
        </Wrapper>
    );
};
